import { useId, useState } from "react";
import type { ReactNode } from "react";
import {
	CoreInfoIndicator,
	CoreMenuItem,
	CoreRadioGroup,
	CoreRequiredIcon,
	CoreSelect,
	CoreSkeletonWrapper,
	CoreTextField,
	VerityColorLogo,
} from "../../core";
import { Typography } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import UnsupportedModal from "../edit-quiz-modals/unsupportedModal";
import styles from "./ThirdPartyStep.module.css";
import { ThirdPartyPlatform } from "../../types/common";
import { Trans } from "react-i18next";
import passImage from "../images/check-pass.svg";
import failImage from "../images/check-fail.svg";
import { Platform } from "@verity/url-matcher";
import type { EditQuizPageProps } from "../editQuiz";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { THIRD_PARTY_IMAGE_MAP } from "./StepIconConfig";
import { defaultValidateUrl, hasNonWhitespace } from "../editQuizHelper";

type ThirdPartyRowProps = {
	loading?: boolean;
	createControl: (id: string) => ReactNode;
	header: ReactNode;
	description: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	descriptionProps?: Omit<TypographyProps, "children">;
};

export function renderUrlValidations(url: string | null | undefined) {
	const urlValidation = defaultValidateUrl(url);
	const allValidationsPassed = Object.values(urlValidation).every((v) => v);

	const renderValidationImage = (isValid: boolean) => (
		<img src={isValid ? passImage : failImage} alt="" aria-hidden="true" />
	);

	return (
		<>
			{!allValidationsPassed && hasNonWhitespace(url) && (
				<div className={styles.urlValidationContainer} style={{ gap: "5px" }} aria-live="polite">
					<div className={styles.validationRow}>
						{renderValidationImage(urlValidation.hasHttp)}
						<Typography className={styles.urlValidationText}>
							{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.httpsError")}
						</Typography>
					</div>
					<div className={styles.validationRow}>
						{renderValidationImage(urlValidation.hasDomain)}
						<Typography className={styles.urlValidationText}>
							{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.domainError")}
						</Typography>
					</div>
					<div className={styles.validationRow}>
						{renderValidationImage(urlValidation.notEmpty)}
						<Typography className={styles.urlValidationText}>
							{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.emptyError")}
						</Typography>
					</div>
					<div className={styles.validationRow}>
						{renderValidationImage(urlValidation.noSpaces)}
						<Typography className={styles.urlValidationText}>
							{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.spaceError")}
						</Typography>
					</div>
					<div className={styles.validationRow}>
						{renderValidationImage(urlValidation.noTrailingSlash)}
						<Typography className={styles.urlValidationText}>
							{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.slashError")}
						</Typography>
					</div>
				</div>
			)}
		</>
	);
}

function ThirdPartyRow({
	createControl,
	description,
	header,
	hint,
	loading,
	required,
	descriptionProps,
}: ThirdPartyRowProps) {
	const id = useId();
	return (
		<CoreSkeletonWrapper loading={loading} height={250}>
			<div className={styles.thirdPartyRow}>
				<div className={styles.thirdPartyLabelWrapper}>
					<div className={styles.titleRow}>
						<Typography variant="h3" id={id}>
							{header}
						</Typography>
						{required && <CoreRequiredIcon />}
						{hint && (
							<CoreInfoIndicator
								size={20}
								hint={hint}
								placement="bottom"
								// `bottom` is already MUI's default; the flip modifier is what lifts the tooltip above the
								// icon when it would overflow the step's scroll container, and up there the modal cuts it off.
								slotProps={{ popper: { modifiers: [{ name: "flip", enabled: false }] } }}
							/>
						)}
					</div>
					<Typography {...descriptionProps}>{description}</Typography>
				</div>
				{createControl(id)}
			</div>
		</CoreSkeletonWrapper>
	);
}

type ThirdPartyStepProps = {
	/**If first time configuring the quiz */
	isPortalMadeThirdPartyAssessment?: boolean;
	url: string | undefined;
	setThirdPartyUrl: (url: string | null) => void;
	isFirstTime: boolean;
	platforms: ThirdPartyPlatform[];
	selectedPlatformId: number | undefined | null;
	setSelectedPlatformId: (platform: number | undefined | null) => void;
	thirdPartyPassword: string | undefined | null;
	setThirdPartyPassword: (password: string | null) => void;
	loading?: boolean;
} & Required<Pick<EditQuizPageProps, "setProctoringOption" | "proctoringOption">>;

export default function ThirdPartyStep(props: ThirdPartyStepProps) {
	const {
		isPortalMadeThirdPartyAssessment,
		url,
		setThirdPartyUrl,
		thirdPartyPassword,
		platforms,
		selectedPlatformId,
		setSelectedPlatformId,
		setThirdPartyPassword,
		isFirstTime,
		proctoringOption,
		setProctoringOption,
		loading,
	} = props;
	const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);
	const [hasPassword, setHasPassword] = useState<boolean>(() => {
		// Undefined means user has yet to made choice
		const hasUserMadeChoice = thirdPartyPassword !== undefined;

		if (isFirstTime && !hasUserMadeChoice) {
			// UI team wants password to default to true on first time
			return true;
		}

		return !!thirdPartyPassword;
	});
	const [enableAnyway, setEnableAnyway] = useState(thirdPartyPassword ? true : false);
	const selectedPlatform =
		typeof selectedPlatformId === "number" ? platforms.find((p) => p.platformId === selectedPlatformId) : undefined;

	return (
		<>
			<StepWrapper>
				<div className={styles.thirdPartyContainer}>
					<StepDescription loading={loading}>
						{i18nPackage.t("editQuizPage.thirdPartyAssessment.description")}
					</StepDescription>
					{isPortalMadeThirdPartyAssessment && (
						<>
							<ThirdPartyRow
								loading={loading}
								header={i18nPackage.t("editQuizPage.thirdPartyAssessment.urlTitle")}
								description={i18nPackage.t("editQuizPage.thirdPartyAssessment.urlDesc")}
								createControl={() => (
									<div style={{ display: "flex", flexDirection: "column", gap: "5px", width: "100%" }}>
										<CoreTextField
											onChange={(e) => {
												const withoutLeadingWhitespace = e.target.value.trimStart();
												setThirdPartyUrl(withoutLeadingWhitespace);
											}}
											value={url ?? ""}
											label={i18nPackage.t("editQuizPage.thirdPartyAssessment.urlPlaceholder")}
										/>
										{renderUrlValidations(url)}
									</div>
								)}
								required
							/>
						</>
					)}
					<ThirdPartyRow
						loading={loading}
						description={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformDesc")}
						header={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformTitle")}
						hint={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformTooltip")}
						createControl={(id) => (
							<CoreSelect
								value={selectedPlatformId ?? ""}
								renderValue={(value) => {
									const platform =
										typeof value === "number" ? platforms.find((p) => p.platformId === value) : undefined;
									const platformKey = platform ? (platform.platformId as Platform) : undefined;

									return (
										<div className={styles.selectLabelWrapper}>
											<Typography component="span">
												{platform
													? platform.platformName
													: i18nPackage.t("editQuizPage.thirdPartyAssessment.platformPlaceholder")}
											</Typography>
											{platformKey && platformKey in THIRD_PARTY_IMAGE_MAP ? THIRD_PARTY_IMAGE_MAP[platformKey] : null}
										</div>
									);
								}}
								onChange={(e) => {
									setSelectedPlatformId(Number(e.target.value));
								}}
								inputProps={{
									"aria-labelledby": id,
								}}
								MenuProps={{
									PaperProps: {
										sx: {
											maxHeight: "200px",
										},
									},
									// MenuList is the element MUI renders with role="listbox", so name it with the row header
									MenuListProps: {
										"aria-labelledby": id,
									},
								}}
								className={styles.platformSelect}
							>
								{platforms.map((platform) => {
									const platformKey = platform.platformId as Platform;

									return (
										<CoreMenuItem key={platform.platformId} value={platform.platformId}>
											<div className={styles.selectLabelWrapper}>
												<Typography component="span">{platform.platformName}</Typography>
												{platformKey in THIRD_PARTY_IMAGE_MAP ? THIRD_PARTY_IMAGE_MAP[platformKey] : null}
											</div>
										</CoreMenuItem>
									);
								})}
							</CoreSelect>
						)}
					/>
					{selectedPlatform && (
						<>
							<ThirdPartyRow
								loading={loading}
								header={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordSwitchTitle")}
								description={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordSwitchDesc")}
								createControl={(id) => (
									<CoreRadioGroup
										radioOptions={[
											{ label: i18nPackage.t("editQuizPage.thirdPartyAssessment.yes"), value: "true" },
											{ label: i18nPackage.t("editQuizPage.thirdPartyAssessment.no"), value: "false" },
										]}
										onChange={(_, value) => {
											const hasPassword = value === "true";
											setHasPassword(hasPassword);
											if (!hasPassword) {
												setThirdPartyPassword(null);
											}
										}}
										selectedValue={String(hasPassword)}
										radioGroupAriaLabelledby={id}
										sx={{ paddingLeft: "9px" }}
									/>
								)}
							/>
							{hasPassword && (
								<ThirdPartyRow
									loading={loading}
									header={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordTitle")}
									description={
										<Trans
											i18n={i18nPackage}
											i18nKey={"editQuizPage.thirdPartyAssessment.passwordDesc"}
											components={{ b: <strong /> }}
											values={{ platform: selectedPlatform.platformName }}
										/>
									}
									descriptionProps={{ component: "span" }}
									createControl={() => (
										<CoreTextField
											onChange={(e) => {
												if (proctoringOption.disableRightClick && !enableAnyway && !thirdPartyPassword) {
													setShowUnsupportedModal(true);
												} else {
													setThirdPartyPassword(e.target.value);
												}
											}}
											value={thirdPartyPassword}
											label={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordPlaceholder")}
											// On inputProps so required lands on the underlying <input>, not MUI's FormControl wrapper
											inputProps={{ required: true }}
										/>
									)}
									required
								/>
							)}
							{!hasPassword && (
								<div className={styles.noPasswordContainer}>
									<VerityColorLogo height={28} width={28} />
									<Typography component={"span"}>
										<Trans
											i18n={i18nPackage}
											i18nKey={"editQuizPage.thirdPartyAssessment.noPasswordWarning"}
											components={{ b: <strong /> }}
											values={{ platform: selectedPlatform.platformName }}
										/>
									</Typography>
								</div>
							)}
						</>
					)}
				</div>
			</StepWrapper>
			<UnsupportedModal
				open={showUnsupportedModal}
				onClose={() => {
					setShowUnsupportedModal(false);
				}}
				onConfirm={() => {
					setProctoringOption?.({ disableRightClick: false });
					setShowUnsupportedModal(false);
				}}
				onEnableAnyway={() => {
					setEnableAnyway(true);
					setShowUnsupportedModal(false);
				}}
				platformUnsupportedOptionNames={{
					passwordConflict: {
						otherActivities: [{ name: "Disable Right Click", option: "disableRightClick" }],
					},
				}}
				enableAnyway
			/>
		</>
	);
}
